import { Injectable, Type } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { KalDialogConfig, KalDialogService, KalSnackbarService } from '@kalidea/kaligraphi';
import { Select, Store } from '@ngxs/store';
import gql from 'graphql-tag';
import isEqual from 'lodash-es/isEqual';
import { BehaviorSubject, forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { LayoutService } from 'src/app/_core/services/layout.service';
import { SubscriptionManager } from 'src/app/_core/tools/subscription-manager';
import {
    ConfirmDialogComponent,
    ConfirmDialogData
} from 'src/app/_shared/dialogs/confirm-dialog/confirm-dialog.component';
import {
    SharedAllocationSettleLoadsDialogComponent,
    SharedAllocationSettleLoadsDialogData
} from 'src/app/_shared/dialogs/shared-allocation-settle-loads-dialog/shared-allocation-settle-loads-dialog.component';
import { AdvancedRule, AdvancedRuleService } from 'src/app/_shared/services/advanced-rule/advanced-rule.service';
import {
    SharedAllocationExecutionSetParamsAction
} from 'src/app/_shared/stores/shared-allocation-execution/shared-allocation-execution.actions';
import { UserState } from 'src/app/_user/store/user/user.state';
import { graphqlErrors } from 'src/app/utils/errors.utils';
import { wheneverEnd } from 'src/app/utils/operators.utils';
import {
    AttributionBudgetModelDto,
    EditerExecutionAttributionAutoModel,
    MutationDupliquerAttributionBudgetArgs,
    StatusDtoEnum,
    TypesRegleEnum
} from 'src/graphql/generated-types';

export type AllocationEdition = {
    allocation: AttributionBudgetModelDto,
    automatedExecution?: EditerExecutionAttributionAutoModel
};

export const AllocationsContext = {
    ATB: 'ATB',
    PMV: 'PMV',
    RAN: 'RAN',
    ART: 'ART'
} as const;

type AdvancedRuleOrNull = AdvancedRule | null;

@Injectable()
export abstract class AllocationService extends SubscriptionManager {

    @Select(UserState.currentDelegation) currentDelegation$: Observable<any>;

    loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    /**
     * Attribution
     */
    abstract allocation$: Observable<AttributionBudgetModelDto>;

    /**
     * Règle avancée d'accessibilité (pour 1 attribution)
     */
    allocationAdvancedAccessibilityRule$: BehaviorSubject<AdvancedRuleOrNull> = new BehaviorSubject<AdvancedRuleOrNull>(null);

    /**
     * Règle avancée de subvention (pour 1 attribution)
     */
    allocationAdvancedSubventionRule$: BehaviorSubject<AdvancedRuleOrNull> = new BehaviorSubject<AdvancedRuleOrNull>(null);

    /**
     * Est-ce que l'enveloppe rattachée à l'attribution est archivée ?
     */
    abstract isAllocationEnvelopeArchived$: Observable<boolean>;

    /**
     * Paramètres d'exécution automatique de l'attribution
     */
    abstract automatedExecution$: Observable<EditerExecutionAttributionAutoModel>;

    /**
     * Type de l'attribution
     * Permet de distinguer les différents types pour des questions d'affichages dans les composants
     */
    abstract type: (typeof AllocationsContext)[keyof typeof AllocationsContext];

    /**
     * Routes exploitées dans les composants :
     * - Liste des attributions
     * - Détail d'une attribution
     * - Edition d'une attribution
     * - Exécution d'une attribution
     */
    abstract routes: { list: string, detail: string, edit: string, execution: string };

    /**
     * Datasource utilisé pour lister le résultat des attributions
     */
    reportDatasource$: BehaviorSubject<any> = new BehaviorSubject(null);

    /**
     * Actions NGXS exploitées:
     * - get: Récupération d'une attribution
     * - save: Enregistrement d'une attribution
     * - delete: Suppression d'une attribution
     * - execute: Execution d'une attribution
     * - cancelExecution: Annulation de l'exécution d'une attribution
     * - reload: Rechargement de la liste des attributions
     */
    abstract actions: {
        get: Type<any>,
        save: Type<any>,
        delete: Type<any>,
        execute: Type<any>,
        cancelExecution: Type<any>,
        reload: Type<any>
    };

    executeLabel = 'Exécuter l\'attribution';

    cancelExecutionLabel = 'Annuler l\'exécution';

    readonly executedI18n: Record<string, string> = {
        [AllocationsContext.PMV]: 'Exécuté',
        other: 'Exécutée'
    };

    constructor(
        protected readonly route: ActivatedRoute,
        protected readonly store: Store,
        protected readonly graphqlService: GraphqlService,
        protected readonly layoutService: LayoutService,
        protected readonly snackbarService: KalSnackbarService,
        protected readonly dialogService: KalDialogService,
        protected readonly advancedRuleService: AdvancedRuleService
    ) {
        super();
    }

    get(allocationId: number, options: { force: boolean } = {force: false}): Observable<any> {
        this.loading$.next(true);
        return this.store.dispatch(new this.actions.get(allocationId, options))
            .pipe(
                tap(() => this.loading$.next(false)),
                catchError(() => {
                    this.loading$.next(false);
                    return this.navigateToList();
                })
            );
    }

    settle$(allocation: AttributionBudgetModelDto): Observable<[any, boolean]> {
        const config = new KalDialogConfig<SharedAllocationSettleLoadsDialogData>({
            title: 'Reprise de charge',
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
            data: {
                allocationId: allocation.Id
            },
        });

        return this.dialogService.open(SharedAllocationSettleLoadsDialogComponent, config).afterClosed
            .pipe(
                filter(data => !!data?.accepted),
                switchMap(() => this.allocation$),
                tap(() => this.loading$.next(true)),
                switchMap(a => forkJoin([
                    this.store.dispatch(new this.actions.reload(a.Id)),
                    this.navigateToDetail(a.Id)
                ])),
                wheneverEnd(() => this.loading$.next(false))
            );
    }

    edit(allocation: AttributionBudgetModelDto): Observable<boolean> {
        return this.confirmAllocationEdition(allocation).pipe(
            switchMap(() => this.navigateToEdit(allocation.Id)),
        );
    }

    save(allocation: AllocationEdition): Observable<[any, boolean]> {
        this.loading$.next(true);
        return this.store.dispatch(new this.actions.save(allocation))
            .pipe(
                tap(() => this.snackbarService.open({
                    title: `L'attribution ${allocation.allocation.Libelle} a bien été enregistrée.`
                })),
                switchMap(() => this.allocation$),
                switchMap((a) => forkJoin([
                    this.store.dispatch(new this.actions.reload(a.Id)),
                    this.navigateToDetail(a.Id)
                ])),
                wheneverEnd(() => this.loading$.next(false))
            );
    }

    duplicate(allocation: AttributionBudgetModelDto): Observable<boolean> {
        this.loading$.next(true);

        return this.graphqlService.mutateOne<'DupliquerAttributionBudget', MutationDupliquerAttributionBudgetArgs>({
            mutation: gql`
                mutation DupliquerAttributionBudget($contexte: ContextInfosInput, $idAtb: Float) {
                    DupliquerAttributionBudget(contexte: $contexte, idAtb: $idAtb) {
                        Id
                    }
                }
            `,
            variables: {
                idAtb: allocation.Id
            }
        }).pipe(
            tap(({Id}) => this.store.dispatch(new this.actions.reload(Id))),
            tap(() => this.snackbarService.open({
                title: `L'attribution ${allocation.Libelle} a bien été dupliquée.`
            })),
            switchMap(({Id}) => this.navigateToEdit(Id)),
            tap(() => this.loading$.next(false)),
            catchError(err => {
                this.loading$.next(false);
                return throwError(err);
            })
        );
    }

    delete(allocation: AttributionBudgetModelDto): Observable<any> {
        if (!!allocation.AttribTgExec) {
            return this.archive(allocation);
        }

        this.loading$.next(true);
        return this.store.dispatch(new this.actions.delete(allocation.Id))
            .pipe(
                tap(() => this.snackbarService.open({
                    title: `L'attribution ${allocation.Libelle} a bien été supprimée.`
                })),
                catchError(err => {
                    const errorCode = graphqlErrors(err, 'code');
                    if (+errorCode === 0) {
                        this.loading$.next(false);
                        return this.archiveProposal(allocation);
                    }
                    return throwError(err);
                }),
                tap(() => this.loading$.next(false)),
                switchMap(() =>
                    this.store.dispatch(new this.actions.reload())
                ),
                switchMap(() => this.navigateToList()),
            );
    }

    archive(allocation: AttributionBudgetModelDto): Observable<any> {
        this.loading$.next(true);

        return this.store.dispatch(new this.actions.save({
            allocation: {
                ...allocation,
                Status: StatusDtoEnum.Updated,
                IsCloture: true
            }
        })).pipe(
            tap(() => this.snackbarService.open({
                    title: `L'attribution ${allocation.Libelle} a bien été archivée.`
                }
            )),
            switchMap(() => this.store.dispatch(new this.actions.reload(allocation.Id))),
            wheneverEnd(() => this.loading$.next(false)),
        );
    }

    execute(allocation: AttributionBudgetModelDto): Observable<boolean> {
        const {Id, Libelle, IsNominative, ArticleModele} = allocation;

        return this.store.dispatch(new SharedAllocationExecutionSetParamsAction({
            code: this.type,
            allocation: {
                id: Id,
                articleId: ArticleModele?.ID,
                label: Libelle,
                isNominatif: IsNominative
            },
        })).pipe(
            switchMap(() => this.navigateToExecution())
        );
    }

    cancelExecution(allocation: AttributionBudgetModelDto): Observable<any> {
        return this.store.dispatch(
            new this.actions.cancelExecution({
                code: this.type,
                allocationId: allocation.Id
            })).pipe(
            switchMap(() => this.initializeReportDatasource$(allocation.Id)),
            tap(() => {
                this.snackbarService.open({title: `L'exécution de l'attribution a bien été annulée`});
            }),
        );
    }

    navigateToList(): Observable<boolean> {
        return this.layoutService.navigate({
            content: this.routes.list,
            focus: 'empty'
        }, 'content');
    }

    navigateToEdit(allocationId): Observable<boolean> {
        return this.layoutService.navigate({
            content: this.routes.list,
            focus: `${this.routes.edit}/${allocationId}`
        }, 'focus');
    }

    navigateToDetail(allocationId): Observable<boolean> {
        return this.layoutService.navigate({
            content: this.routes.list,
            focus: `${this.routes.detail}/${allocationId}`
        }, 'focus');
    }

    navigateToExecution(): Observable<boolean> {
        return this.layoutService.navigate({
            focus: this.routes.execution
        }, 'focus');
    }

    /**
     * Initialization spécifique du datasource utilisé pour lister le résultat d'une attribution
     */
    abstract initializeReportDatasource$(id?: number): Observable<any>;

    initialize$(): Observable<any> {
        return this.route.paramMap.pipe(
            map((params: ParamMap) => +params.get('id')),
            switchMap(attributionId => this.get(attributionId)),
            switchMap(() => this.allocation$),
            distinctUntilChanged(isEqual),
            switchMap((allocation) => {
                if (!allocation?.AttribTgExec) {
                    this.reportDatasource$?.next(null);
                    return of(allocation);
                }

                this.loading$.next(true);
                return this.initializeReportDatasource$(allocation.Id).pipe(
                    tap(() => this.loading$.next(false)),
                    map(() => allocation)
                );
            }),
            switchMap(allocation => {
                this.loading$.next(true);

                const hasSubventionAdvancedRule = !!allocation?.MoteurRegleSubQta?.IsRegleAvancee;

                return forkJoin([
                    !!allocation?.MoteurRegleAcc?.IsRegleAvancee
                        ? this.advancedRuleService.getAdvancedRule$(allocation?.IdRglAcc, TypesRegleEnum.Accessibilite)
                        : of(null),
                    hasSubventionAdvancedRule
                        ? this.advancedRuleService.getAdvancedRule$(allocation?.IdRglSubQta, TypesRegleEnum.Subvention)
                        : of(null),
                    hasSubventionAdvancedRule
                        ? this.advancedRuleService.getRoundingTypesList$()
                        : of(null)
                ]);
            }),
            tap(([advancedAccessibilityRule, advancedSubventionRule]) => {
                this.loading$.next(false);
                this.allocationAdvancedAccessibilityRule$.next(advancedAccessibilityRule?.Regle || null);
                this.allocationAdvancedSubventionRule$.next(advancedSubventionRule?.Regle || null);
            })
        )
    }

    protected archiveProposal(allocation: AttributionBudgetModelDto): Observable<boolean> {
        const config: KalDialogConfig<ConfirmDialogData> = {
            title: `Archiver l'attribution`,
            backdropClass: 'cdk-overlay-dark-backdrop',
            hasBackdrop: true,
            data: {
                paragraphs: [
                    `L'attribution ${allocation.Libelle} ne peut pas être supprimée.`,
                    'Voulez-vous archiver l\'attribution ?'
                ],
                submitLabel: 'Archiver',
                cancelLabel: 'Annuler'
            }
        };

        return this.dialogService.open(ConfirmDialogComponent, config).afterClosed.pipe(
            filter(result => !!result?.accepted),
            switchMap(() => this.archive(allocation))
        );
    }

    protected confirmAllocationEdition({
                                           IsAttribBoule,
                                           AttribTgExec,
                                           Libelle
                                       }: AttributionBudgetModelDto): Observable<ConfirmDialogData> {
        if (!IsAttribBoule || AttribTgExec) {
            return of(null);
        }

        const config: KalDialogConfig<ConfirmDialogData> = {
            title: 'Modification d\'attribution générée par Essentiel',
            backdropClass: 'cdk-overlay-dark-backdrop',
            hasBackdrop: true,
            data: {
                paragraphs: [
                    `L'attribution ${Libelle} a été générée depuis Essentiel.`,
                    'En cas de modification, les règles d\'accessibilités (pour qui) et de subventions (combien) seront supprimées et devront être reparamétrées'
                ],
                submitLabel: 'Continuer',
                cancelLabel: 'Annuler'
            }
        };

        return this.dialogService.open(ConfirmDialogComponent, config).afterClosed.pipe(
            filter(result => !!result?.accepted)
        );
    }
}
